import { getColumnSourceSeparator, getReportSourcePrefix } from "config";

const SERVER_ONLY_CHILD_FIELDS = [
  "notForOutput",
  "expectedColumns",
  "requiredEmbeddedReports",
  "valid",
  "updated",
  "created",
  "userId",
  "readOnly",
  "allUsers",
];

const normaliseName = (name) => (name ?? "").trim().toLowerCase();

// True when a report carries a parent; null and absent are the same.
export function hasDuplicateOf(report) {
  return (
    typeof report?.duplicateOf === "string" && report.duplicateOf.length > 0
  );
}

// The source id a parent occupies in a specification.
export function parentSourceId(parent) {
  return parent.kind === "table"
    ? parent.internalName
    : `${getReportSourcePrefix()}${parent.internalName}`;
}

// Recovers a duplication's parent kind from its own specification.
export function parentKindOf(entry) {
  const [source] = entry.specification?.sourceTables ?? [];
  return typeof source === "string" &&
    source.startsWith(getReportSourcePrefix())
    ? "report"
    : "table";
}

// Builds the local report that stands for a duplicate of parent.
export function buildDuplicationEntry(
  parent,
  parentColumns,
  name,
  preAggregationFilters
) {
  const trimmed = name.trim();
  const sourceTable = parentSourceId(parent);
  const specification = {
    sourceTables: [sourceTable],
    sourceColumns: parentColumns.map((c) => ({
      sourceTable,
      sourceColumn: c.internalName,
      column: c.internalName,
    })),
    outputColumns: parentColumns.map((c) => ({ column: c.internalName })),
  };
  if (preAggregationFilters?.length) {
    specification.preAggregationFilters = preAggregationFilters;
  }
  return {
    duplicateOf: parent.internalName,
    displayName: trimmed,
    description: `A duplication of ${parent.displayName}`,
    internalName: trimmed,
    type: "SINGLE_TABLE_REPORT",
    specification,
  };
}

// Turns a saved report's notForOutput child back into a duplication entry.
// The embedding itself is done by useSaveReport.flattenReferencedReport.
export function fromEmbeddedChild(child) {
  const entry = { ...child };
  for (const field of SERVER_ONLY_CHILD_FIELDS) delete entry[field];
  const prefix = getReportSourcePrefix();
  if (entry.internalName?.startsWith(prefix)) {
    entry.internalName = entry.internalName.slice(prefix.length);
  }
  return entry;
}

// A duplicate name is available when it is non-blank and not already the
// internal or display name of a table, saved report or duplication.
export function isDuplicateNameAvailable(
  name,
  tables,
  savedReports,
  duplications
) {
  const candidate = normaliseName(name);
  if (!candidate) return false;
  const taken = new Set();
  for (const source of [
    ...(tables ?? []),
    ...(savedReports ?? []),
    ...(duplications ?? []),
  ]) {
    taken.add(normaliseName(source.internalName));
    taken.add(normaliseName(source.displayName));
  }
  return !taken.has(candidate);
}

// True when name is the non-blank internal or display name of entry.
export function isOwnDuplicateName(name, entry) {
  const candidate = normaliseName(name);
  if (!candidate || !entry) return false;
  return (
    candidate === normaliseName(entry.internalName) ||
    candidate === normaliseName(entry.displayName)
  );
}

// Rewrites every reference to a renamed duplicate: its entry in the selected
// report list, the source table of any output column drawn from it, and either
// operand of any join against it.
export function renameDuplicateSource(
  oldInternalName,
  newInternalName,
  { selectedReports, outputColumns, joinConfigs }
) {
  const oldSource = `${getReportSourcePrefix()}${oldInternalName}`;
  const newSource = `${getReportSourcePrefix()}${newInternalName}`;
  const renameSource = (table) => (table === oldSource ? newSource : table);
  return {
    selectedReports: (selectedReports ?? []).map((id) =>
      id === oldInternalName ? newInternalName : id
    ),
    outputColumns: (outputColumns ?? []).map((col) => {
      if (col.sourceTable !== oldSource) return col;
      // The display name may carry the old name as a source qualifier, added
      // when two sources contributed the same name; it moves with the rename.
      const oldSuffix = `${getColumnSourceSeparator()}${oldInternalName}`;
      const displayName =
        typeof col.displayName === "string" &&
        col.displayName.endsWith(oldSuffix)
          ? `${col.displayName.slice(
              0,
              -oldSuffix.length
            )}${getColumnSourceSeparator()}${newInternalName}`
          : col.displayName;
      return { ...col, sourceTable: newSource, displayName };
    }),
    joinConfigs: (joinConfigs ?? []).map((config) =>
      config.leftTable === oldSource || config.rightTable === oldSource
        ? {
            ...config,
            leftTable: renameSource(config.leftTable),
            rightTable: renameSource(config.rightTable),
          }
        : config
    ),
  };
}

export function sortDuplications(entries) {
  return [...entries].sort((a, b) =>
    (a.displayName ?? "").localeCompare(b.displayName ?? "")
  );
}

export function duplicationsOf(parentInternalName, duplications) {
  return sortDuplications(
    (duplications ?? []).filter((e) => e.duplicateOf === parentInternalName)
  );
}
